import Styles from './FileName.module.scss'
import { useHistory } from 'react-router-dom'
import { useEvent } from 'react-use'
import { TreeFile } from 'screens/Finder/FinderContextTypes'
import { getArticlePath } from 'utils/urlUtils'
import { EditPropertyEvent, editPropertyEventName } from '../../FileTree/events/editProperty'
import { Text } from 'blaze-ui/Text/Text'
import { PostFloatingPreview } from 'screens/Calendar/PostFloatingPreview/PostFloatingPreview'
import { FloatingPopover } from 'components/FloatingPopover/FloatingPopover'
import { useState } from 'react'
import { PollScheduledPostStatus } from 'components/ArticlePostPreview/PostPreviewBody/PollScheduledPostStatus/PollScheduledPostStatus'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { capitalize } from 'lodash'
import { CalendarEventData } from 'screens/Calendar/types'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType } from 'api/clients/PropertyClient'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface Props {
  file: TreeFile
  onClick?: () => void
  onDelete?: (fileId: string) => void
}

export const FileName = ({ file, onClick, onDelete }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const history = useHistory()
  const { refresh } = useFinderContext()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const [hideFloatingPreview, setHideFloatingPreview] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const scheduledPost = file.scheduled_post

  const { status, postAt } = usePostProperties({
    id: scheduledPost?.source_id as string,
    type: scheduledPost?.source_type as AssigneeType,
  })

  useEvent(
    editPropertyEventName,
    (e) => {
      const evt = e as EditPropertyEvent
      const { anchorFile } = evt.detail
      if (anchorFile.id !== file.id) return
      if (file.slug) history.push(getArticlePath({ slug: file.slug }))
    },
    document.body,
  )

  const onClose = () => {
    setHideFloatingPreview(true)
  }

  const eventData = {
    start: postAt,
    id: file.id,
    title: file.title,
    asset_type: capitalize(file.type),
    post_at: scheduledPost?.post_at,
    image:
      scheduledPost?.media_objects?.[0]?.thumbnail_url ||
      scheduledPost?.media_objects?.[0]?.source_url ||
      file?.preview_url,
    duration: scheduledPost?.media_objects?.[0]?.duration,
    post_url: scheduledPost?.post_url,
    scheduled_post_id: scheduledPost?.id,
    external_integration_id: scheduledPost?.external_integration?.id,
    scheduled_post_provider: scheduledPost?.external_integration?.provider,
    status: status.value,
    key: file.id,
    postable: true,
    created_at: file.created_at,
    content: file.text_preview,
    channel: file.channel,
  } as CalendarEventData

  if (isMobile) {
    return (
      <div className={Styles.title} key={file.key}>
        <div className={Styles.title}>
          <Text lineClamp={1} variant="primary" color="var(--dark-80)">
            {file.title}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <>
      <FloatingPopover
        placement="top"
        hide={hideFloatingPreview}
        content={
          !!file.project_id ? undefined : (
            <PostFloatingPreview
              eventData={eventData}
              onClose={onClose}
              withinFloatingPopover
              onIntegrationSelect={refresh}
              onDelete={() => queryClient.invalidateQueries(['workspace', currentWorkspaceId, 'files'])}
              onDeleteFail={() => queryClient.invalidateQueries(['workspace', currentWorkspaceId, 'files'])}
              onOptimisticDelete={() => onDelete?.(file.id)}
            />
          )
        }
      >
        <div className={Styles.title} key={file.key} onClick={onClick}>
          <div className={Styles.title}>
            <Text lineClamp={1} variant={isSystemRedesignEnabled ? 'primary' : 'largeList'} color="var(--dark-80)">
              {file.title}
            </Text>
          </div>
        </div>
      </FloatingPopover>

      {scheduledPost && <PollScheduledPostStatus scheduledPost={scheduledPost} />}
    </>
  )
}
